import { usePageSeo } from "@/hooks/usePageSeo";
import Navbar from "@/components/Navbar";
import StatsSection from "@/components/StatsSection";
import FeaturesSection from "@/components/FeaturesSection";
import CTASection from "@/components/CTASection";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Target, Heart, Eye, Zap, Users, Award, Clock } from "lucide-react";

const values = [
  { icon: Target, title: "Reliability", description: "We never miss a pickup. Track your driver in real-time and travel with confidence." },
  { icon: Heart, title: "Hospitality", description: "Warm Dominican hospitality from booking to drop-off. We treat every guest like family." },
  { icon: Eye, title: "Transparency", description: "Fixed prices, no hidden fees. What you see is what you pay — always." },
  { icon: Zap, title: "Innovation", description: "Smart booking technology, real-time tracking, and instant confirmations." },
];

const AboutPage = () => {
  usePageSeo("about");
  return (
    <main className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <p className="text-primary font-semibold text-sm uppercase tracking-widest mb-3">About Us</p>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                Meet Always On Time
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8 max-w-lg">
                Your trusted partner for premium tours and transportation across the Dominican Republic, delivering exceptional Caribbean experiences since day one.
              </p>

              <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                  { icon: Users, value: "5000+", label: "Happy Travelers" },
                  { icon: Award, value: "5-Star", label: "Service Rating" },
                  { icon: Clock, value: "24/7", label: "Available" },
                ].map((stat) => (
                  <div key={stat.label} className="text-center bg-white rounded-lg p-4 border border-border">
                    <stat.icon className="w-6 h-6 text-primary mx-auto mb-2" />
                    <div className="font-display font-bold text-foreground text-lg">{stat.value}</div>
                    <div className="text-muted-foreground text-xs">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg p-6 border border-border">
                <p className="text-foreground italic text-base mb-2">
                  "Every journey should be memorable, not just the destination."
                </p>
                <p className="text-primary font-semibold text-sm">— Founder, Always On Time Transfers Tours</p>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <img
                src="https://media.viajando.travel/p/ff85334a07385327281e625ba104b1c8/adjuntos/236/imagenes/000/742/0000742421/republica-dominicana-isla-saona-ocean-tours-rd-2jpg.jpg"
                alt="AOT Transfers vehicle"
                className="w-full h-96 object-cover rounded-lg"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">Our Story</h2>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-4 text-muted-foreground leading-relaxed">
            <p>Always On Time Transfers Tours was born from a passion for the Dominican Republic and a desire to share its beauty with the world. We saw travelers struggling with unreliable transportation and knew we could offer something better.</p>
            <p>Today, we operate a fleet of premium vehicles and offer curated tours across the island's most stunning destinations. From pristine beaches to historic cities, we make every journey unforgettable.</p>
            <p>Whether you're here for vacation, a group getaway, or adventure tours, Always On Time is your trusted Caribbean travel partner.</p>
          </motion.div>
        </div>
      </section>

      <StatsSection />

      {/* Mission */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="max-w-3xl mx-auto rounded-lg p-10 md:p-14 text-center bg-primary">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-foreground mb-6">Our Mission</h2>
            <p className="text-primary-foreground/90 text-lg leading-relaxed">
              To be the most trusted tour and transfer service in the Dominican Republic, delivering exceptional Caribbean experiences through professionalism, safety, and genuine hospitality.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <p className="text-primary font-semibold text-sm uppercase tracking-widest mb-2">What Drives Us</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">Our Core Values</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {values.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="text-center p-6 bg-white rounded-lg border border-border hover:border-primary/30 hover:shadow-md transition-all duration-300">
                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center mx-auto mb-4">
                  <v.icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <h3 className="font-display text-base font-bold text-foreground mb-2">{v.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{v.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FeaturesSection />
      <CTASection />
      <Footer />
    </main>
  );
};

export default AboutPage;
